import { useMemo, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Columns3, List } from 'lucide-react';
import { Button, Card, Input, Spin, Typography, Modal, Tooltip } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';

import { getContactsQueryKey, listContacts } from '../../contacts/api/contacts.client.js';
import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import { updateDealStage, type DealStage } from '../api/deals.client.js';
import { DealListView } from '../components/DealListView.js';
import { KanbanBoard } from '../components/KanbanBoard.js';
import { useCreateDealMutation } from '../hooks/useCreateDealMutation.js';
import { useDeals } from '../hooks/useDeals.js';
import { useDealStageMutation } from '../hooks/useDealStageMutation.js';
import { buildKanbanColumns, STAGE_ORDER } from '../lib/kanban.js';
import {
  clearSelection,
  DEFAULT_DEAL_FILTERS,
  filterDealsForWorkbench,
  getFailedSelection,
  getActiveFilterCount,
  toggleSelection,
  type DealViewMode,
  type DealWorkbenchFilters,
} from '../lib/workbench.js';

const { Title, Text } = Typography;

export function DealKanbanPage(): ReactElement {
  const scope = useCurrentScope();
  const { deals, isLoading, isError, error, refetch } = useDeals();
  const stageMutation = useDealStageMutation();
  const createDealMutation = useCreateDealMutation();
  const [viewMode, setViewMode] = useState<DealViewMode>('kanban');
  const [filters, setFilters] = useState<DealWorkbenchFilters>(DEFAULT_DEAL_FILTERS);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchStage, setBatchStage] = useState<DealStage>('Contacted');
  const [batchError, setBatchError] = useState<string | null>(null);
  const [isBatchMoving, setIsBatchMoving] = useState(false);
  const [createStage, setCreateStage] = useState<DealStage | null>(null);
  const [newDealTitle, setNewDealTitle] = useState('');
  const [newDealAmount, setNewDealAmount] = useState('');
  const [newDealContactId, setNewDealContactId] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const contactQuery = useQuery({
    queryKey: getContactsQueryKey(workspaceId),
    queryFn: () => listContacts(),
    enabled: createStage !== null,
  });

  const visibleDeals = useMemo(() => filterDealsForWorkbench(deals, filters), [deals, filters]);
  const activeFilterCount = useMemo(() => getActiveFilterCount(filters), [filters]);
  const columns = useMemo(() => buildKanbanColumns(visibleDeals), [visibleDeals]);
  const activeDealsCount = useMemo(() => visibleDeals.filter((deal) => deal.stage !== 'Won' && deal.stage !== 'Lost').length, [visibleDeals]);
  const needsAttentionCount = useMemo(
    () => visibleDeals.filter((deal) => deal.stage !== 'Won' && deal.stage !== 'Lost' && deal.nextActivityDue === null).length,
    [visibleDeals],
  );

  async function handleBatchMove(): Promise<void> {
    const ids = [...selectedIds];
    if (ids.length === 0) {
      return;
    }

    setIsBatchMoving(true);
    setBatchError(null);
    const results = await Promise.allSettled(ids.map((dealId) => updateDealStage(dealId, { toStage: batchStage })));
    const failedIds = new Set(ids.filter((_, index) => results[index]?.status === 'rejected'));

    if (failedIds.size > 0) {
      setSelectedIds(getFailedSelection(ids, failedIds));
      setBatchError(`${failedIds.size} сделок не перемещено`);
    } else {
      setSelectedIds(clearSelection(selectedIds));
    }

    setIsBatchMoving(false);
    await refetch();
  }

  async function handleCreateDeal(): Promise<void> {
    if (createStage === null || newDealTitle.trim().length === 0 || newDealContactId.length === 0) {
      return;
    }

    try {
      setCreateError(null);
      await createDealMutation.mutateAsync({
        amount: Number(newDealAmount) || 0,
        contactId: newDealContactId,
        stage: createStage,
        title: newDealTitle.trim(),
      });
    } catch {
      setCreateError('Не удалось создать сделку. Проверьте контакт и попробуйте ещё раз.');
      return;
    }

    setCreateStage(null);
    setNewDealTitle('');
    setNewDealAmount('');
    setNewDealContactId('');
  }

  function openCreateForm(stage: DealStage): void {
    setCreateStage(stage);
    setCreateError(null);
  }

  if (isLoading) {
    return (
      <main className="mx-auto flex h-full items-center justify-center p-6 text-sm">
        <Spin size="large" />
        <Text style={{ marginLeft: 16 }}>Загрузка сделок...</Text>
      </main>
    );
  }

  if (isError) {
    return (
      <main className="mx-auto max-w-3xl p-6 h-full flex items-center justify-center">
        <Card style={{ width: '100%', maxWidth: 480, textAlign: 'center' }}>
          <Title level={4}>Не удалось загрузить сделки</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
            {error?.message ?? 'Попробуйте повторить запрос.'}
          </Text>
          <Button type="primary" aria-label="Повторить" onClick={() => void refetch()} icon={<ReloadOutlined />}>Повторить</Button>
        </Card>
      </main>
    );
  }

  return (
    <div className="animate-fade-in flex flex-col h-full gap-6">
      {/* Header Area */}
      <header className="flex justify-between items-center shrink-0 z-10 w-full">
        <div>
          <h1 className="font-bold text-[#2d2418] m-0 mb-1 leading-tight tracking-tight" style={{ fontSize: 28 }}>
            Сделки
          </h1>
          <Text type="secondary" style={{ fontSize: 13, marginTop: 4, display: 'block' }}>
            <span data-testid="deals-visible-count">{visibleDeals.length} из {deals.length}</span> сделок · кросс-проектный вид
          </Text>
        </div>
        <div className="flex items-center gap-3">
          {/* View toggle */}
          <div className="flex items-center p-1 rounded-lg" style={{ background: '#e7e5e4', border: '1px solid #d6d3d1' }}>
            <Tooltip title="Канбан">
              <Button
                type="text"
                size="small"
                aria-label="Канбан"
                aria-pressed={viewMode === 'kanban'}
                onClick={() => setViewMode('kanban')}
                style={{
                  minWidth: 32,
                  height: 28,
                  padding: 0,
                  backgroundColor: viewMode === 'kanban' ? '#fff' : 'transparent',
                  color: viewMode === 'kanban' ? '#d97706' : '#a8a29e',
                  boxShadow: viewMode === 'kanban' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                  borderRadius: 6
                }}
              >
                <Columns3 size={14} />
              </Button>
            </Tooltip>
            <Tooltip title="Список">
              <Button
                type="text"
                size="small"
                aria-label="Список"
                aria-pressed={viewMode === 'list'}
                onClick={() => setViewMode('list')}
                style={{
                  minWidth: 32,
                  height: 28,
                  padding: 0,
                  backgroundColor: viewMode === 'list' ? '#fff' : 'transparent',
                  color: viewMode === 'list' ? '#d97706' : '#a8a29e',
                  boxShadow: viewMode === 'list' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                  borderRadius: 6
                }}
              >
                <List size={14} />
              </Button>
            </Tooltip>
          </div>
          
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreateForm('New')} style={{ borderRadius: 8, height: 36, padding: '0 16px', fontWeight: 500 }}>
            Новая сделка
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => void refetch()} style={{ borderRadius: 8, height: 36, width: 36 }} />
        </div>
      </header>

      <div className="flex-1 flex flex-col min-h-0 gap-6">
        {/* Filter bar */}
        <div className="flex items-center gap-4 bg-white px-4 py-3 border border-[#e7e5e4] shadow-sm rounded-2xl w-full shrink-0">
          <div className="flex-1 max-w-[400px]">
            <Input
              value={filters.query}
              onChange={(event) => setFilters((current) => ({ ...current, query: event.target.value }))}
              data-testid="deals-search-input"
              aria-label="Поиск сделок"
              placeholder="Поиск по сделке или контакту"
              allowClear
              variant="borderless"
              className="bg-[#f5f5f4] hover:bg-[#e7e5e4] focus:bg-white transition-colors"
              style={{ borderRadius: 8, padding: '8px 12px' }}
            />
          </div>

          <div className="flex items-center gap-3 ml-auto">
            {/* Native select to keep playwright tests happy */}
            <select
              value={filters.stage}
              onChange={(event) => setFilters((current) => ({ ...current, stage: event.target.value as DealStage | 'all' }))}
              aria-label="Фильтр по стадии"
              className="bg-transparent text-[#44403c] text-[14px] font-medium hover:text-[#2d2418] transition-colors"
              style={{
                height: 36,
                border: '1px solid #e7e5e4',
                borderRadius: 8,
                padding: '0 12px',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="all">Все стадии</option>
              {STAGE_ORDER.map((stage) => (
                <option key={stage} value={stage}>{stage}</option>
              ))}
            </select>

            <select
              value={filters.activity}
              onChange={(event) =>
                setFilters((current) => ({ ...current, activity: event.target.value as DealWorkbenchFilters['activity'] }))
              }
              aria-label="Фильтр по активности"
              className="bg-transparent text-[#44403c] text-[14px] font-medium hover:text-[#2d2418] transition-colors"
              style={{
                height: 36,
                border: '1px solid #e7e5e4',
                borderRadius: 8,
                padding: '0 12px',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="all">Все активности</option>
              <option value="due">Есть следующий шаг</option>
              <option value="missing">Без следующего шага</option>
            </select>

            {activeFilterCount > 0 ? (
              <Button type="text" size="small" onClick={() => setFilters(DEFAULT_DEAL_FILTERS)} style={{ color: '#ef4444' }}>
                Сбросить фильтры ({activeFilterCount})
              </Button>
            ) : null}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <Card size="small" bordered={false}>
            <Text type="secondary">Всего сделок</Text>
            <Title level={3} data-testid="deals-summary-total" style={{ margin: 0 }}>{visibleDeals.length}</Title>
          </Card>
          <Card size="small" bordered={false}>
            <Text type="secondary">Активные</Text>
            <Title level={3} data-testid="deals-summary-active" style={{ margin: 0 }}>{activeDealsCount}</Title>
          </Card>
          <Card size="small" bordered={false}>
            <Text type="secondary">Требуют внимания</Text>
            <Title level={3} data-testid="deals-summary-attention" style={{ margin: 0 }}>{needsAttentionCount}</Title>
          </Card>
        </div>

        {/* Selected bar */}
        {selectedIds.size > 0 ? (
          <Card size="small" style={{ backgroundColor: '#fffbeb', borderColor: '#fcd34d' }} styles={{ body: { padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 12 } }} data-testid="deals-bulk-bar">
            <Text style={{ fontWeight: 600, color: '#d97706' }}>{selectedIds.size} выбрано</Text>
            
            <select
              value={batchStage}
              onChange={(event) => setBatchStage(event.target.value as DealStage)}
              aria-label="Переместить выбранные сделки"
              style={{
                height: 28,
                border: '1px solid #d9d9d9',
                borderRadius: 4,
                padding: '0 8px',
                fontSize: 12,
                backgroundColor: '#ffffff',
                outline: 'none',
                cursor: 'pointer',
                marginLeft: 8
              }}
            >
              {STAGE_ORDER.map((stage) => (
                <option key={stage} value={stage}>{stage}</option>
              ))}
            </select>
            
            <Button size="small" onClick={() => void handleBatchMove()} disabled={isBatchMoving}>
              Переместить
            </Button>
            <Button size="small" type="text" onClick={() => setSelectedIds(new Set())}>
              Отменить
            </Button>
            {batchError !== null ? <Text type="danger">{batchError}</Text> : null}
          </Card>
        ) : null}

        {/* Board content */}
        <div className="flex-1 min-h-0">
          {viewMode === 'kanban' ? (
            <KanbanBoard
              columns={columns}
              isUpdating={stageMutation.isPending}
              onCreateInStage={openCreateForm}
              onMoveDeal={(dealId: string, toStage: DealStage) => {
                stageMutation.mutate({ dealId, toStage });
              }}
              onToggleSelected={(dealId) => setSelectedIds((current) => toggleSelection(current, dealId))}
              selectedIds={selectedIds}
            />
          ) : (
            <DealListView
              deals={visibleDeals}
              isUpdating={stageMutation.isPending}
              onMoveDeal={(dealId: string, toStage: DealStage) => {
                stageMutation.mutate({ dealId, toStage });
              }}
              onToggleSelected={(dealId) => setSelectedIds((current) => toggleSelection(current, dealId))}
              selectedIds={selectedIds}
            />
          )}
        </div>
      </div>

      <Modal
        title="Новая сделка"
        open={createStage !== null}
        onCancel={() => {
          setCreateStage(null);
          setCreateError(null);
        }}
        footer={[
          <Button key="cancel" onClick={() => setCreateStage(null)}>
            Отменить
          </Button>,
          <Button key="submit" type="primary" loading={createDealMutation.isPending} onClick={() => void handleCreateDeal()}>
            Создать
          </Button>,
        ]}
        width={420}
        destroyOnClose
      >
        <div className="flex flex-col gap-4 mt-4">
          <div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Название</Text>
            <Input
              aria-label="Название сделки"
              value={newDealTitle}
              onChange={(event) => setNewDealTitle(event.target.value)}
            />
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Сумма</Text>
            <Input
              aria-label="Сумма"
              inputMode="numeric"
              value={newDealAmount}
              onChange={(event) => setNewDealAmount(event.target.value)}
            />
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Контакт</Text>
            <select
              aria-label="Контакт"
              value={newDealContactId}
              onChange={(event) => setNewDealContactId(event.target.value)}
              style={{
                width: '100%',
                height: 32,
                border: '1px solid #d9d9d9',
                borderRadius: 6,
                padding: '0 11px',
                fontSize: 14,
                backgroundColor: '#ffffff',
                outline: 'none',
              }}
            >
              <option value="">Выберите контакт</option>
              {(contactQuery.data ?? []).map((contact) => (
                <option key={contact.id} value={contact.id}>
                  {contact.fullName}
                </option>
              ))}
            </select>
          </div>
          {createError !== null ? <Text type="danger" style={{ fontSize: 13 }}>{createError}</Text> : null}
        </div>
      </Modal>
    </div>
  );
}
